import { z } from 'zod';

// Import existing schemas from other entities to avoid duplication
import { AddressSchema } from '../address';
import { StoreSchema } from '../store';
import { PaymentSchema } from '../payment/type';
import { DeviceSchema, PriceSchema, SubscriptionSchema } from '../shared';

// Basic Customer schema (used in list/search operations)
export const CustomerSchema = z.object({
    id: z.string(),
    email: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    externalCustomerId: z.string().nullable().optional(),
    lastOrderId: z.string().nullable(),
    accountId: z.string(),
    storeId: z.string(),
    billingAddress: AddressSchema.optional(),
    shippingAddress: AddressSchema.optional(),
    currency: z.string().nullable(),
    locale: z.string().nullable(),
    draft: z.boolean().default(false),
    acceptsMarketing: z.boolean().default(false),
    createdAt: z.date().nullable(),
    updatedAt: z.date().nullable(),
    metadata: z.any().optional(),
    // Store as nested object
    store: z.object({
        id: z.string(),
        name: z.string(),
    }),
    // PaymentInstrument with card info
    paymentInstrument: z.object({
        id: z.string(),
        type: z.string(),
        card: z.any(),
    }).nullable().optional(),
    // Primary device info for list view
    device: DeviceSchema.nullable().optional(),
});

// Customer Detail schema (used in getCustomerById with relations)
export const CustomerDetailSchema = z.object({
    id: z.string(),
    email: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    externalCustomerId: z.string().nullable().optional(),
    lastOrderId: z.string().nullable(),
    accountId: z.string(),
    storeId: z.string(),
    billingAddress: AddressSchema.optional(),
    shippingAddress: AddressSchema.optional(),
    currency: z.string().nullable(),
    locale: z.string().nullable(),
    draft: z.boolean().default(false),
    acceptsMarketing: z.boolean().default(false),
    createdAt: z.date().nullable(),
    updatedAt: z.date().nullable(),
    metadata: z.any().optional(),
    // Relations
    devices: z.array(DeviceSchema).optional(),
    store: StoreSchema.optional(),
    payments: z.array(PaymentSchema).optional(),
    subscriptions: z.array(SubscriptionSchema).optional(),
});

// Customer List Item schema (used in listCustomers output)
export const CustomerListItemSchema = z.object({
    id: z.string(),
    storeId: z.string(),
    storeName: z.string(),
    email: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    createdAt: z.date().nullable(),
    lastOrderId: z.string().nullable(),
    paymentInstrumentCardLast4: z.string().nullable(),
});

// Export types
export type Customer = z.infer<typeof CustomerSchema>;
export type CustomerDetail = z.infer<typeof CustomerDetailSchema>;
export type CustomerListItem = z.infer<typeof CustomerListItemSchema>;

// Re-export schemas and types from other entities for convenience
export { AddressSchema } from '../address';
export type Address = z.infer<typeof AddressSchema>;

export { StoreSchema } from '../store';
export type { Store } from '../store';

export { PaymentSchema } from '../payment/type';
export type { Payment } from '../payment/type';

export { DeviceSchema, PriceSchema, SubscriptionSchema } from '../shared';
export type { Device, Price, Subscription } from '../shared';
